import { useEffect, useId, useRef, useState } from 'react';
import { dialogTabStops } from './dialog-tab-stops';
import { ArrowUpRight, FileSearch, LoaderCircle, Search, X } from 'lucide-react';
import type { PageSearchResult } from '../shared/desktop-api';
import './document-search-dialog.css';

export type DocumentSearchDialogProps = {
  jobId: string;
  batchId: string;
  jobName: string;
  /** True only when the original navigation owner accepted this exact page. */
  onChoose: (result: PageSearchResult) => boolean;
  onClose: () => void;
};
type SearchState = {
  identity: string;
  status: 'loading' | 'ready' | 'error';
  results: PageSearchResult[];
  error?: string;
};

/** Read-only search presentation. The caller owns document navigation and any
 * existing draft admission; a result never authorizes a write. */
export function DocumentSearchDialog({
  jobId,
  batchId,
  jobName,
  onChoose,
  onClose,
}: DocumentSearchDialogProps) {
  const [query, setQuery] = useState('');
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<SearchState | null>(null);
  const dialog = useRef<HTMLDialogElement>(null),
    input = useRef<HTMLInputElement>(null);
  const openingFocus = useRef<{ target: HTMLElement | null } | null>(null);
  const focusGeneration = useRef(0),
    requestGeneration = useRef(0);
  const mounted = useRef(false),
    closing = useRef(false);
  const navigationAccepted = useRef(false);
  const heading = useId(),
    help = useId();
  const trimmedQuery = query.trim();
  const identity = JSON.stringify([jobId, batchId, trimmedQuery]);
  // Render ownership closes the gap before an old effect's cleanup runs.
  const liveIdentity = useRef(identity);
  liveIdentity.current = identity;
  const current = state?.identity === identity ? state : null;
  const eligible = trimmedQuery.length >= 2 && Boolean(jobId && batchId);
  const busy = eligible && (!current || current.status === 'loading');
  const results = eligible && current?.status === 'ready' ? current.results : [];
  const dismiss = () => {
    if (closing.current) return;
    closing.current = true;
    requestGeneration.current++;
    onClose();
  };
  const choose = (result: PageSearchResult) => {
    if (
      closing.current ||
      !mounted.current ||
      liveIdentity.current !== identity ||
      result.batchId !== batchId ||
      current?.status !== 'ready' ||
      !current.results.includes(result)
    )
      return;
    // An accepted jump owns focus, even if React reuses the original field node
    // for another page/document. A refused target leaves this dialog usable.
    let accepted = false;
    navigationAccepted.current = true;
    try {
      accepted = onChoose(result);
    } finally {
      navigationAccepted.current = accepted;
    }
    if (accepted) {
      closing.current = true;
      requestGeneration.current++;
    }
  };

  useEffect(() => {
    const generation = ++focusGeneration.current;
    mounted.current = true;
    openingFocus.current ??= {
      target: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    };
    const previousFocus = openingFocus.current.target,
      element = dialog.current;
    element?.showModal();
    input.current?.focus({ preventScroll: true });
    return () => {
      mounted.current = false;
      requestGeneration.current++;
      // Return editor focus outside React cleanup; StrictMode replay retains the
      // original opener, and a successor modal or new interaction takes priority.
      queueMicrotask(() => {
        if (focusGeneration.current !== generation) return;
        element?.close();
        if (navigationAccepted.current) return;
        if (
          !(previousFocus instanceof HTMLElement) ||
          !previousFocus.isConnected ||
          document.querySelector('dialog[open]')
        )
          return;
        const active = document.activeElement;
        if (
          active === document.body ||
          active === previousFocus ||
          !active?.isConnected ||
          (active && element?.contains(active))
        )
          previousFocus.focus({ preventScroll: true });
      });
    };
  }, []);

  useEffect(() => {
    const generation = ++requestGeneration.current;
    if (!eligible) return;
    let cancelled = false;
    const owns = () =>
      !cancelled &&
      mounted.current &&
      !closing.current &&
      generation === requestGeneration.current &&
      liveIdentity.current === identity;
    setState({ identity, status: 'loading', results: [] });
    const timer = window.setTimeout(async () => {
      if (!owns()) return;
      try {
        const api = window.capturePointDesktop;
        if (!api?.searchPages) throw new Error('Document search is unavailable on this window.');
        const found = await api.searchPages({ jobId, batchId, query: trimmedQuery, limit: 30 });
        if (!owns()) return;
        // The native request chooses the job's current batch. Never expose a
        // different batch's hits if that owner changed while the read ran.
        if (found.some((result) => result.batchId !== batchId)) {
          setState({
            identity,
            status: 'error',
            results: [],
            error: 'Search isn’t available for these documents yet.',
          });
          return;
        }
        setState({ identity, status: 'ready', results: found.slice(0, 30) });
      } catch (reason: unknown) {
        if (owns())
          setState({
            identity,
            status: 'error',
            results: [],
            error: reason instanceof Error ? reason.message : 'The search could not finish.',
          });
      }
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [identity, eligible, retry]);

  return (
    <dialog
      ref={dialog}
      className="document-search-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={heading}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          dismiss();
      }}
      onCancel={(event) => {
        event.preventDefault();
        dismiss();
      }}
      onKeyUp={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        // Chromium's search input otherwise consumes Escape to clear its value
        // before the native dialog can cancel. Our advertised key closes it.
        if (event.key === 'Escape') {
          event.preventDefault();
          dismiss();
          return;
        }
        if (event.key !== 'Tab') return;
        const controls = dialogTabStops(event.currentTarget);
        const first = controls[0],
          last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
    >
      <header className="document-search-heading">
        <div>
          <h2 id={heading}>Search documents</h2>
          <p>{jobName}</p>
        </div>
        <button type="button" onClick={dismiss} aria-label="Close document search">
          <X size={21} />
        </button>
      </header>
      <div className="document-search-query">
        <Search size={21} />
        <input
          ref={input}
          type="search"
          aria-label="Search documents"
          aria-describedby={help}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find a name, number or phrase"
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' && results.length) {
              event.preventDefault();
              dialog.current?.querySelector<HTMLButtonElement>('.document-search-result')?.focus();
            }
          }}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear document search"
            onClick={() => {
              setQuery('');
              input.current?.focus();
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>
      <div className="document-search-results" aria-busy={busy}>
        {!eligible ? (
          <div className="document-search-empty">
            <FileSearch size={35} />
            <strong>Find it in your documents.</strong>
            <p id={help}>Type at least 2 characters to search the current batch.</p>
          </div>
        ) : busy ? (
          <div className="document-search-message" role="status">
            <LoaderCircle className="spin" size={21} />
            <span>Searching documents…</span>
          </div>
        ) : current?.status === 'error' ? (
          <div className="document-search-error" role="alert" data-telemetry="error:search-failed">
            <strong>Search could not finish</strong>
            <p>{current.error}</p>
            <button type="button" onClick={() => setRetry((value) => value + 1)}>
              Try again
            </button>
          </div>
        ) : results.length === 0 ? (
          <div className="document-search-empty" role="status">
            <FileSearch size={35} />
            <strong>No matches</strong>
            <p>Try another name, number or phrase.</p>
          </div>
        ) : (
          <>
            <p className="document-search-count" role="status">
              {results.length} {results.length === 1 ? 'matching page' : 'matching pages'}
              {results.length === 30 ? ' shown' : ''}
            </p>
            <ul
              aria-label="Matching document pages"
              onKeyDown={(event) => {
                if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const items = [
                    ...event.currentTarget.querySelectorAll<HTMLButtonElement>('button'),
                  ],
                  index = items.indexOf(document.activeElement as HTMLButtonElement);
                if (event.key === 'ArrowUp' && index === 0) {
                  input.current?.focus();
                  return;
                }
                const next =
                  event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? items.length - 1
                      : event.key === 'ArrowDown'
                        ? Math.min(index + 1, items.length - 1)
                        : Math.max(0, index - 1);
                items[next]?.focus();
              }}
            >
              {results.map((result, index) => (
                <li key={`${result.documentId}:${result.pageNumber}:${index}`}>
                  <button
                    type="button"
                    className="document-search-result"
                    onClick={() => choose(result)}
                  >
                    <span className="document-search-result-heading">
                      <strong>{result.sourceName}</strong>
                      <span>
                        Page {result.pageNumber}
                        <ArrowUpRight size={16} />
                      </span>
                    </span>
                    <p>{result.snippet.replace(/[‹›]/g, '')}</p>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <footer className="document-search-footer">
        <span id={eligible ? help : undefined}>Current batch · up to 30 matching pages</span>
        <span>
          <kbd>Esc</kbd> Close
        </span>
      </footer>
    </dialog>
  );
}
